import React, { useState, forwardRef, useEffect } from "react";
import { StyleSheet } from "react-native";
import { Modal } from "~/components/Modal";
import { Spacer } from "~/components/Spacer";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import type { TouchableOpacityProps } from "~/components/TouchableOpacity";
import { View } from "~/components/View";
import { styleConstants as C } from "~/style/styleConstants";
import { isPromise } from "~/utils/isPromise";
import { shadow } from "~/utils/shadow";
import { Pressable } from "./Pressable";
import type { ReactNode } from "react";
import type { TextStyle } from "react-native";

export interface ButtonProps extends TouchableOpacityProps {
  title?: string;
  isLoading?: boolean | undefined;
  small?: boolean;
  medium?: boolean;
  large?: boolean;
  outline?: boolean;
  transparent?: boolean;
  colorDanger?: boolean;
  colorSuccess?: boolean;
  colorTheme?: boolean;
  colorDark?: boolean;
  colorLight?: boolean;
  colorWhite?: boolean;
  colorBlack?: boolean;

  hasShadow?: boolean;

  children?: ReactNode;
  onPress?:
    | TouchableOpacityProps["onPress"]
    | ((...args: any[]) => Promise<any>);
  blockUi?: boolean;
}
export type Button = typeof Button;

export const Button = forwardRef<Pressable, ButtonProps>(
  (
    {
      title,

      isLoading: isLoadingProp,

      small = false,
      medium = false,
      large = false,

      outline = false,
      transparent = false,
      colorDanger = false,
      colorSuccess = false,
      colorTheme = false,
      colorDark = false,
      colorLight = false,
      colorWhite = false,
      colorBlack = false,

      hasShadow = false,

      style: inheritedStyle,
      disabled,
      children,
      onPress,
      blockUi = true,
      ...props
    },
    ref,
  ) => {
    const isMounted = React.useRef<boolean>(true);
    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);

    const shouldRenderTitle = typeof title === "string";

    const resolveBackgroundColor = () => {
      if (outline) return "transparent";
      if (transparent) return "transparent";

      if (disabled) return C.colorBackgroundDisabled;
      else if (colorDanger) return C.colorBackgroundDanger;
      else if (colorSuccess) return C.colorBackgroundSuccess;
      else if (colorTheme) return C.colorBackgroundTheme;
      else if (colorDark) return C.colorBackgroundDark;
      else if (colorLight) return C.colorBackgroundLight;
      else if (colorWhite) return C.colorBackgroundWhite;
      else if (colorBlack) return C.colorBackgroundBlack;

      return C.colorBackgroundTheme;
    };

    const resolveHeight = () => {
      if (small) return 32;
      if (medium) return 40;
      if (large) return 48;
      return 40;
    };

    const resolveTextStyle = () => {
      const style: TextStyle = {};
      if (small) style.fontSize = C.fontSizeExtraSmall;
      if (large) style.fontSize = C.fontSizeMedium;
      else style.fontSize = C.fontSizeMedium;

      if (disabled) style.color = C.colorTextDarkSofter;
      else if (outline || transparent) {
        if (colorDanger) style.color = C.colorTextDanger;
        else if (colorSuccess) style.color = C.colorTextSuccess;
        else if (colorTheme) style.color = C.colorTextTheme;
        else if (colorDark) style.color = C.colorTextDark;
        else if (colorLight) style.color = C.colorTextLight;
        else if (colorWhite) style.color = C.colorBackgroundWhite;
        else if (colorBlack) style.color = C.colorBackgroundBlack;
        else style.color = C.colorTextTheme;
      } else {
        if (colorLight) style.color = C.colorTextDark;
        else if (colorWhite) style.color = C.colorBackgroundBlack;
        else style.color = C.colorTextLight;
      }

      return style;
    };

    const resolveBorderColor = () => {
      if (outline && colorDark) return C.colorTextDarkSofter;
      if (outline) return resolveTextStyle().color;
      if (transparent) return "transparent";
      if (disabled) return C.colorBackgroundDisabled;
      if (colorDanger) return C.colorBackgroundDanger;
      if (colorSuccess) return C.colorBackgroundSuccess;
      if (colorTheme) return C.colorBackgroundTheme;
      if (colorDark) return C.colorBackgroundDark;
      if (colorLight) return C.colorBackgroundLight;
      if (colorWhite) return C.colorBackgroundWhite;
      if (colorBlack) return C.colorBackgroundBlack;
      return C.colorBackgroundTheme;
    };

    const resolveShadow = () => {
      return 0;
    };

    const [isLoadingState, setIsLoading] = useState(false);
    const isLoading = isLoadingProp || isLoadingState;

    const borderRadius = 8; // shared between the button and the spinner overlay
    const style: TouchableOpacityProps["style"] = {
      flexDirection: "row",
      justifyContent: "center", // ideja kod dodavanja ikona -> children != null ? "flex-start" : "center"
      alignItems: "center",
      backgroundColor: resolveBackgroundColor(),
      borderColor: resolveBorderColor(),
      borderWidth: 1,
      height: resolveHeight(),
      paddingHorizontal: C.spacingMedium,
      borderRadius,
      ...shadow(resolveShadow()),
      opacity: isLoading ? 0.5 : 1,
    };

    const textStyle = resolveTextStyle();

    return (
      <>
        <Pressable
          ref={ref as any}
          style={({ pressed }) => [
            style,
            inheritedStyle,
            hasShadow ? { ...shadow(2) } : undefined,
            {
              opacity: pressed ? 0.5 : 1,
            },
          ]}
          onPress={(event) => {
            if (typeof onPress === "function") {
              const maybePromise = onPress(event);

              if (isPromise(maybePromise)) {
                setIsLoading(true);

                maybePromise.finally(() => {
                  if (isMounted.current) {
                    setIsLoading(false);
                  }
                });
              }
            }
          }}
          disabled={isLoading || disabled}
          {...props}
        >
          <>
            {children}
            {Boolean(children && shouldRenderTitle) && <Spacer small />}
            {shouldRenderTitle && (
              <Text numberOfLines={1} weightRegular style={textStyle}>
                {title}
              </Text>
            )}
          </>

          {isLoading && (
            <View
              centerContent
              style={{ ...StyleSheet.absoluteFillObject, borderRadius }}
            >
              <Spinner size="small" color={C.colorTextDarkSoft} />
            </View>
          )}
        </Pressable>

        {Boolean(blockUi && isLoading) && (
          <Modal blockHardwareBackButton>
            <Pressable>
              <View flex />
            </Pressable>
          </Modal>
        )}
      </>
    );
  },
);
